<template>
  <div class="floating-input-wrapper">
    <div
      class="floating-input"
      :class="{
        'floating-input--focused': isFocused,
        'floating-input--success': state === 'success',
        'floating-input--error': state === 'error',
        'floating-input--has-value': hasValue,
      }"
    >
      <div v-if="icon" class="input-icon">
        <img :src="icon" alt="" width="24" height="24" />
      </div>
      <div class="input-body">
        <label class="input-label" :for="inputId">{{ label }}</label>
        <input
          :id="inputId"
          ref="input"
          class="input-field"
          :type="effectiveType"
          :inputmode="inputmode"
          :value="value"
          :dir="inputDir"
          @input="$emit('input', $event.target.value)"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>
      <button
        v-if="passwordToggle"
        type="button"
        class="input-eye"
        :aria-label="passwordVisible ? 'הסתר סיסמא' : 'הצג סיסמא'"
        @click="togglePasswordVisibility"
      >
        <img :src="passwordVisible ? eyeOffIcon : eyeIcon" alt="" width="24" height="24" />
      </button>
      <div v-else-if="state === 'success'" class="input-check">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00C070" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
    </div>
    <p v-if="state === 'error' && errorMessage" class="input-error">{{ errorMessage }}</p>
  </div>
</template>

<script>
let inputCounter = 0;

const eyeIcon = require('@/pages/responsive-pages/non-authenticated/mortgage-refinance/assets/lucide-eye.svg');
const eyeOffIcon = require('@/pages/responsive-pages/non-authenticated/mortgage-refinance/assets/lucide-eye-off.svg');

export default {
  name: 'FloatingInput',
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    inputmode: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: 'default',
      validator: v => ['default', 'success', 'error'].includes(v),
    },
    errorMessage: {
      type: String,
      default: '',
    },
    inputDir: {
      type: String,
      default: 'rtl',
    },
    passwordToggle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    inputCounter += 1;
    return {
      isFocused: false,
      inputId: `floating-input-${inputCounter}`,
      passwordVisible: false,
      eyeIcon,
      eyeOffIcon,
    };
  },
  computed: {
    hasValue() {
      return this.value && this.value.length > 0;
    },
    effectiveType() {
      if (this.passwordToggle) {
        return this.passwordVisible ? 'text' : 'password';
      }
      return this.type;
    },
  },
  methods: {
    onFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },
    onBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },
    focus() {
      this.$refs.input.focus();
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
  },
};
</script>

<style lang="scss" scoped>
.floating-input-wrapper {
  width: 100%;
}

.floating-input {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 74px;
  background: #fff;

  @media (max-width: 500px) {
    height: 67px;
  }
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 19px;
  box-shadow: 0px 20px 29.4px rgba(182, 173, 130, 0.27);
  padding: 0 20px;
  gap: 10px;
  direction: rtl;
  transition: border-color 0.2s ease, border-width 0.2s ease;

  &--focused {
    border: 2px solid #5D7AFD;
    padding: 0 19px; // compensate for thicker border
  }

  &--success {
    border: 2px solid #00C070;
    padding: 0 19px;
  }

  &--error {
    border: 2px solid #FF653F;
    padding: 0 19px;
  }
}

.input-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
}

.input-body {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.input-label {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #205441;
  opacity: 0.45;
  pointer-events: none;
  transition: transform 0.2s ease, font-size 0.2s ease, opacity 0.2s ease, top 0.2s ease;

  .floating-input--focused &,
  .floating-input--has-value & {
    top: 12px;
    transform: translateY(0);
    font-size: 12px;
    opacity: 0.35;
  }
}

.input-field {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #205441;
  outline: none;
  padding: 0;
  padding-top: 20px;
  text-align: right;
}

.input-check {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-eye {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #205441;

  img {
    display: block;
  }
}

.input-error {
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #FF653F;
  text-align: right;
  margin-top: 8px;
  padding-right: 8px;
  animation: fadeInUp 0.2s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
